import { customElement, property, state } from 'lit/decorators.js'
import { consume } from '@lit/context'
import { styleMap } from 'lit/directives/style-map.js'
import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { productTileProductContext } from 'components/discovery/product/tile-parts/tile-product-context.js'
import { InternalProduct } from 'components/discovery/product/tile-parts/tile-parts-typing.js'
import { style } from './featured-attributes.style.js'

type FeaturedAttributeItem = {
  value: string;
  picture: string;
  tooltip?: string;
  color?: string;
  displayMode?: FeaturedAttributeDisplayMode | null;
  display_mode?: FeaturedAttributeDisplayMode | null
}

export const fontVariants = ['body', 'body-s', 'caption-l', 'caption-m', 'caption-s'] as const
export type fontVariantsType = typeof fontVariants

export const featuredAttributeDisplayModes = ['icon', 'text', 'icon_text'] as const
export type FeaturedAttributeDisplayMode = typeof featuredAttributeDisplayModes[number]

@customElement('csc-featured-attributes')
export class CSCFeaturedAttributes extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @consume({ context: productTileProductContext })
  @state()
  private _tileProduct: InternalProduct | undefined = undefined

  @property({ type: Array })
  items: FeaturedAttributeItem[] = []

  @property({ type: Boolean })
  inline = true

  @property({ type: String, attribute: 'list-variant' })
  listVariant: 'unstyled' | 'bullet' | 'custom-icon' = 'unstyled'

  @property({ type: String, attribute: 'icon-name' })
  iconName: string = 'star'

  @property({ type: String, attribute: 'icon-style' })
  iconStyle: string = 'regular'

  @property({ type: Boolean, attribute: 'divider' })
  divider = true

  @property({ type: String, attribute: 'font-variant' })
  fontVariant: fontVariantsType[number] = 'body-s'

  @property({ type: String, attribute: 'display-mode' })
  displayMode?: FeaturedAttributeDisplayMode

  isValidSVG = (svgString: string) => {
    const parser = new DOMParser()
    const doc = parser.parseFromString(svgString, 'image/svg+xml')

    const svgElement = doc.querySelector('svg')
    const parserError = doc.getElementsByTagName('parsererror')

    return svgElement !== null && parserError.length === 0
  }

  isValidURL = (urlString: string) => {
    try {
      new URL(urlString)
      return true
    } catch {
      return false
    }
  }

  // Always rendered as cse-text's "prefix" slot content now (see render() — even icon-only
  // items wrap their icon in cse-text) so it picks up cse-text's font-size cascade for the
  // configured variant.
  defineAttributePictureType = (attributePicture: string, attributeValue: string, attributeColor?: string) => {
    switch (true) {
      case this.isValidSVG(attributePicture): {
        const encodedSvg = encodeURIComponent(attributePicture)
        const svgDataUri = `data:image/svg+xml;charset=utf-8,${encodedSvg}`

        return html`
          <cse-image
            slot="prefix"
            source="${svgDataUri}"
            alt="${attributeValue}"
            width="20"
            height="20"
          ></cse-image>
        `
      }

      case this.isValidURL(attributePicture): {
        return html`
          <cse-image
            slot="prefix"
            source="${attributePicture}"
            alt="${attributeValue}"
            width="20"
            height="20"
          ></cse-image>
        `
      }

      case !attributePicture: {
        return nothing
      }

      default: {
        initializeIconsByRole({
          [`csc-featured-attribute-${attributeValue}.icon`]: { name: attributePicture, style: 'light' } as any
        })

        return html`
          <cse-icon
            slot="prefix"
            icon-role="csc-featured-attribute-${attributeValue}.icon"
            size="md"
            style=${styleMap({
              ...(attributeColor ? { '--cse-icon-color': attributeColor } : {}),
            })}
          ></cse-icon>
        `
      }
    }
  }

  override render() {
    const contextItems: FeaturedAttributeItem[] | undefined = this._tileProduct?.featuredAttributes.map((attribute) => ({
      value: attribute.value,
      picture: attribute.picture ?? '',
      displayMode: attribute.display_mode,
    }))
    const sourceItems = contextItems ?? this.items
    const items = this.displayMode
      ? sourceItems.filter((attribute) => (attribute.displayMode ?? attribute.display_mode) === this.displayMode)
      : sourceItems
    if (items.length === 0) {
      return nothing
    }

    return html`
      <cse-list
        .inline=${this.inline}
        .divider=${this.inline && this.divider}
      >
        ${items.map((attribute) => {
          // An 'icon' display_mode attribute is icon-only by definition — never show its text,
          // even if the backend also sent a value. This is the only way to hide an attribute's
          // text now — there's no separate widget-wide hideText override any more. The icon
          // still always renders inside cse-text (just with an empty default slot) rather than
          // bare, so it picks up the same variant-driven font-size cascade text/icon_text items
          // get — a bare cse-icon has no font-size context of its own to scale its em-based
          // size against, which is why it used to stay a fixed size while fontVariant changed
          // everything else.
          const isIconOnly = (attribute.displayMode ?? attribute.display_mode) === 'icon'
          const tooltipText = attribute.tooltip
          const pictureContent = this.defineAttributePictureType(attribute.picture, attribute.value, attribute.color)
          const hasPicture = Boolean(attribute.picture)

          const content = html`
            <cse-text ?has-icon=${hasPicture} variant="${this.fontVariant}" class="cse-text">
              ${hasPicture ? html`${pictureContent}` : nothing}
              ${isIconOnly ? nothing : attribute.value}
            </cse-text>
          `

          return html`
            <cse-list-item
              variant=${this.listVariant}
              icon-name="${this.iconName}"
              icon-style="${this.iconStyle}"
            >
              ${tooltipText
                ? html`
                  <cse-tooltip position="top">
                    <span slot="anchor" class="attribute-content-anchor">
                      ${content}
                    </span>
                    ${tooltipText}
                  </cse-tooltip>
                `
                : content}
            </cse-list-item>
          `
        })}
      </cse-list>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-featured-attributes': CSCFeaturedAttributes
  }
}
